/**
 * Shared ReactQuill config for case/support-desk notes (rich text + embedded images as data URLs).
 */

export function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
}

/** True when the editor has no meaningful text and no embedded images. */
export function isQuillNoteEmpty(html: string): boolean {
  if (!html || !String(html).trim()) return true;
  if (stripHtml(html) !== '') return false;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.querySelector('img') === null;
}

const MAX_IMAGE_BYTES = 2.5 * 1024 * 1024;

/** Toolbar + image handler (file → data URL) for Quill; use as `modules={NOTE_QUILL_MODULES}`. */
export const NOTE_QUILL_MODULES = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
    handlers: {
      image: function (this: { quill: { getSelection: (b: boolean) => { index: number; length: number } | null; getLength: () => number; insertEmbed: (i: number, type: string, value: string, source: string) => void; setSelection: (i: number) => void } }) {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;
          if (file.size > MAX_IMAGE_BYTES) {
            window.alert('Image is too large. Maximum size is 2.5 MB.');
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const url = reader.result as string;
            const quill = this.quill;
            const range = quill.getSelection(true);
            const index = range ? range.index : Math.max(0, quill.getLength() - 1);
            quill.insertEmbed(index, 'image', url, 'user');
            quill.setSelection(index + 1);
          };
          reader.readAsDataURL(file);
        };
      },
    },
  },
};
